import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

const inFlightPostRequests = new Map();

function parseErrorMessage(errorPayload, fallback) {
  if (!errorPayload) return fallback;
  if (typeof errorPayload === 'string') return errorPayload;
  return errorPayload.error || errorPayload.message || fallback;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function runWithConcurrency(items, worker, concurrency = 3) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return;

  const limit = Math.max(1, Math.min(list.length, Number.isFinite(concurrency) ? concurrency : 3));
  let nextIndex = 0;
  let firstError = null;

  const runner = async () => {
    while (nextIndex < list.length && !firstError) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      try {
        await worker(list[currentIndex], currentIndex);
      } catch (error) {
        firstError = error;
      }
    }
  };

  await Promise.all(Array.from({ length: limit }, () => runner()));
  if (firstError) throw firstError;
}

function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;

  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function compactObject(input) {
  if (Array.isArray(input)) {
    return input
      .map((item) => compactObject(item))
      .filter((item) => item !== undefined && item !== null);
  }
  if (input && typeof input === 'object') {
    const out = {};
    Object.entries(input).forEach(([key, value]) => {
      const compacted = compactObject(value);
      if (compacted !== undefined && compacted !== null) {
        out[key] = compacted;
      }
    });
    return out;
  }
  if (input === undefined || input === null) return undefined;
  return input;
}

function toIdempotencyKey(input) {
  const source = String(input || '');
  let hash = 5381;
  for (let i = 0; i < source.length; i += 1) {
    hash = ((hash << 5) + hash + source.charCodeAt(i)) >>> 0;
  }
  return `ml-${hash.toString(16)}`;
}

function shouldSendIdempotencyHeader(endpoint) {
  if (typeof window === 'undefined') return false;
  try {
    const endpointUrl = new URL(endpoint, window.location.origin);
    return endpointUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

async function fetchJsonWithTimeout(endpoint, options, timeoutMs = 25000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(endpoint, {
      ...options,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => null);
    return { response, data };
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function postJSONWithRateLimitRetry(
  endpoint,
  body,
  { maxRetries = 4, timeoutMs = 25000, dedupeKey = '' } = {}
) {
  const inFlightKey = dedupeKey || `${endpoint}::${stableStringify(body)}`;
  if (inFlightPostRequests.has(inFlightKey)) {
    return inFlightPostRequests.get(inFlightKey);
  }

  const requestPromise = (async () => {
    let attempt = 0;
    const idempotencyKey = toIdempotencyKey(inFlightKey);

    while (attempt <= maxRetries) {
      const headers = {
        ...DEFAULT_HEADERS,
      };
      if (shouldSendIdempotencyHeader(endpoint)) {
        headers['x-idempotency-key'] = idempotencyKey;
      }

      const { response, data } = await fetchJsonWithTimeout(
        endpoint,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        },
        timeoutMs
      );
      if (response.ok) return data;

      const errMsg = parseErrorMessage(data, `Request failed with status ${response.status}`);
      const isRateLimited = response.status === 429 || errMsg.toLowerCase().includes('rate limit');

      if (!isRateLimited || attempt === maxRetries) {
        throw new Error(errMsg);
      }

      const retryAfterHeader = Number(response.headers.get('retry-after'));
      const retryAfterMs =
        Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader * 1000 : 0;
      const exponentialMs = 500 * 2 ** attempt;
      const jitterMs = Math.floor(Math.random() * 250);
      await sleep(Math.max(retryAfterMs, exponentialMs + jitterMs));
      attempt += 1;
    }

    throw new Error('Rate limit exceeded');
  })();

  inFlightPostRequests.set(inFlightKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    inFlightPostRequests.delete(inFlightKey);
  }
}

const KG_CACHE_PREFIX = 'animated_lesson_kg_v1';
const LESSON_CACHE_PREFIX = 'animated_lesson_content_v1';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export const AnimatedLessonService = {
  enrichDemoDetailForTopic(topic, node, detail, index = 0) {
    const topicText = String(topic || '').toLowerCase();
    const current = detail && typeof detail === 'object' ? { ...detail } : {};
    const hasStructuredVisual = Boolean(
      current?.pedagogicalDiagram ||
        current?.teachingDiagram ||
        current?.diagramSpec ||
        current?.equations ||
        current?.mathEquations ||
        current?.formulas
    );
    if (hasStructuredVisual) return current;

    const nodeTitle = String(node?.title || node?.id || `Node ${index + 1}`);

    if (topicText.includes('differential equation')) {
      current.demoPlan = {
        kind: 'PEDAGOGICAL_DIAGRAM',
        reason: 'Differential equations are best taught with equations + slope/solution charts.',
        animationEmphasis: 'progressive_draw',
      };
      current.equations = [
        '\\\\frac{dy}{dt}=ky',
        'y(t)=y_0e^{kt}',
        '\\\\frac{d^2x}{dt^2}+2\\\\zeta\\\\omega_n\\\\frac{dx}{dt}+\\\\omega_n^2x=0',
      ];
      current.pedagogicalDiagram = {
        variant: 'chart',
        title: `${nodeTitle}: Solution Behavior`,
        subtitle: 'Exponential growth and decay by parameter sign',
        durationSec: 6,
        chart: {
          type: 'line',
          labels: ['t0', 't1', 't2', 't3', 't4'],
          series: [
            { name: 'k > 0', values: [1, 1.6, 2.5, 4.0, 6.4] },
            { name: 'k < 0', values: [1, 0.62, 0.38, 0.24, 0.15] },
          ],
        },
      };
      return current;
    }

    if (topicText.includes('middle east')) {
      current.demoPlan = {
        kind: 'PEDAGOGICAL_DIAGRAM',
        reason: 'History topics need timelines and dependency maps of events.',
        animationEmphasis: 'stagger_in',
      };
      current.pedagogicalDiagram = {
        variant: 'graph',
        title: `${nodeTitle}: Historical Causality`,
        subtitle: 'How key events connect over time',
        durationSec: 6,
        graph: {
          nodes: [
            { id: 'a', label: 'Empire Decline', xk: 0.18, yk: 0.35 },
            { id: 'b', label: 'Colonial Borders', xk: 0.42, yk: 0.35 },
            { id: 'c', label: 'State Formation', xk: 0.66, yk: 0.35 },
            { id: 'd', label: 'Modern Conflict', xk: 0.82, yk: 0.62 },
          ],
          edges: [
            { from: 'a', to: 'b', label: 'power vacuum', directed: true },
            { from: 'b', to: 'c', label: 'new boundaries', directed: true },
            { from: 'c', to: 'd', label: 'unresolved tensions', directed: true },
          ],
        },
      };
      return current;
    }

    if (topicText.includes('education systems')) {
      current.demoPlan = {
        kind: 'PEDAGOGICAL_DIAGRAM',
        reason: 'Education history benefits from era-by-era comparison tables.',
        animationEmphasis: 'progressive_draw',
      };
      current.pedagogicalDiagram = {
        variant: 'table',
        title: `${nodeTitle}: Education Era Shift`,
        subtitle: 'How access, method, and personalization changed',
        durationSec: 6,
        table: {
          headers: ['Era', 'Access', 'Delivery', 'Assessment'],
          rows: [
            ['Classical', 'Elite', 'Oral/Manuscript', 'Master Judgment'],
            ['Industrial', 'Mass', 'Standard Classroom', 'Standard Exams'],
            ['Digital', 'Broad', 'Online Platforms', 'Mixed Metrics'],
            ['AI Era', 'Adaptive', 'Personalized Paths', 'Continuous Mastery'],
          ],
          caption: 'The trend moves from uniform delivery to adaptive learning.',
        },
      };
      return current;
    }

    if (topicText.includes('artificial intelligence')) {
      current.demoPlan = current.demoPlan || {
        kind: 'PEDAGOGICAL_DIAGRAM',
        reason: 'AI concepts should be shown via structured system diagrams.',
        animationEmphasis: 'progressive_draw',
      };
      current.pedagogicalDiagram = current.pedagogicalDiagram ||
        current.diagramSpec || {
          variant: 'graph',
          title: `${nodeTitle}: AI System Flow`,
          subtitle: 'Model, retrieval, tools, verification',
          durationSec: 6,
          graph: {
            nodes: [
              { id: 'm', label: 'Model', xk: 0.48, yk: 0.24 },
              { id: 'r', label: 'Retriever', xk: 0.24, yk: 0.54 },
              { id: 't', label: 'Tools', xk: 0.5, yk: 0.54 },
              { id: 'v', label: 'Verifier', xk: 0.76, yk: 0.54 },
            ],
            edges: [
              { from: 'r', to: 'm', label: 'context', directed: true },
              { from: 't', to: 'm', label: 'actions', directed: true },
              { from: 'm', to: 'v', label: 'output check', directed: true },
            ],
          },
        };
      return current;
    }

    return current;
  },

  buildFallbackSceneTreeResponse(context = {}, reason = '') {
    const topic = String(context?.topic || 'Lesson Topic').trim() || 'Lesson Topic';
    return {
      lessonId: `scene_fallback_${Date.now().toString(36)}`,
      title: `${topic} Scene Lesson`,
      fallbackReason: reason || 'Failed to generate treeslide content.',
      treeSlides: [
        {
          id: 'fallback_topic',
          title: topic,
          type: 'text',
          physics: { enabled: false, mode: 'none', params: {} },
          timeline: { waitForAnimationPlan: true, requireSignalDone: false, maxWaitSec: 10 },
          children: [],
        },
      ],
    };
  },

  buildSceneFallbackNodeDetail(node, errorMessage = '') {
    const fallbackMessage =
      typeof errorMessage === 'string' && errorMessage.trim()
        ? errorMessage.trim()
        : 'The model did not return valid slide content for this node.';

    const physicsMode = node?.physics?.enabled ? node?.physics?.mode || 'none' : 'none';
    return {
      topicId: node?.id || '',
      explanation: 'Content unavailable. This is a fallback slide.',
      keyPoints: ['Content unavailable. This is a fallback slide.', fallbackMessage],
      examples: [],
      bridgeFromPrevious: '',
      bridgeToNext: '',
      demoPlan: {
        kind: 'NONE',
        reason: 'Fallback content due to node generation failure.',
        animationEmphasis: 'reveal',
      },
      scene: {
        layout: { mode: 'full_text', params: { source: 'fallback' } },
        animationPlan: [
          { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.25 },
          { step: 2, target: 'body', effect: 'typewriter', durationSec: 0.8 },
        ],
        timeline: {
          waitForAnimationPlan: node?.timeline?.waitForAnimationPlan ?? true,
          requireSignalDone: node?.timeline?.requireSignalDone ?? false,
          maxWaitSec: node?.timeline?.maxWaitSec ?? 10,
        },
        physics: {
          enabled: !!node?.physics?.enabled,
          mode: physicsMode,
          params: node?.physics?.params || {},
        },
        layers: {
          background: true,
          content: true,
          highlight: true,
          transition: true,
          debug: false,
        },
      },
    };
  },

  buildKnowledgeGraphCacheKey({ courseId, unitId, topic }) {
    const normalizedTopic = String(topic || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    return `${KG_CACHE_PREFIX}:${courseId || 'no_course'}:${unitId || 'no_unit'}:${normalizedTopic}`;
  },

  buildLessonContentCacheKey({
    courseId,
    unitId,
    topic,
    lessonObjective,
    presentationMode = 'visual',
  }) {
    const normalizedTopic = String(topic || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    const normalizedObjective = String(lessonObjective || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');
    const normalizedMode = String(presentationMode || 'visual')
      .toLowerCase()
      .trim();
    return `${LESSON_CACHE_PREFIX}:${normalizedMode}:${courseId || 'no_course'}:${unitId || 'no_unit'}:${normalizedTopic}:${normalizedObjective}`;
  },

  getCachedKnowledgeGraph(cacheKey) {
    if (!isBrowser() || !cacheKey) return null;
    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  getCachedLessonContent(cacheKey) {
    if (!isBrowser() || !cacheKey) return null;
    try {
      const raw = window.localStorage.getItem(cacheKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  saveInitialKnowledgeGraph(cacheKey, knowledgeGraphResponse) {
    if (!isBrowser() || !cacheKey || !knowledgeGraphResponse) return;
    try {
      if (!window.localStorage.getItem(cacheKey)) {
        window.localStorage.setItem(cacheKey, JSON.stringify(knowledgeGraphResponse));
      }
    } catch {
      // noop
    }
  },

  saveLessonContent(cacheKey, lessonContentResponse) {
    if (!isBrowser() || !cacheKey || !lessonContentResponse) return;
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(lessonContentResponse));
    } catch {
      // noop
    }
  },

  async generateKnowledgeGraphLesson(payload) {
    const endpoint = `${API_BASE_URL}/lessons/animated-knowledge-graph`;

    const attempts = [
      payload,
      {
        topic: payload?.topic || '',
        courseTitle: payload?.courseTitle || '',
        lessonObjective: payload?.lessonObjective || '',
        learner: payload?.learner || {
          name: '',
          educationLevel: '',
          interests: '',
          careerGoals: '',
        },
      },
      {
        topic: payload?.topic || '',
        courseTitle: payload?.courseTitle || '',
        lessonObjective: payload?.lessonObjective || '',
      },
      {
        topic: payload?.topic || '',
      },
    ];

    let lastError = null;
    for (let i = 0; i < attempts.length; i++) {
      const body = attempts[i];
      const { response, data } = await fetchJsonWithTimeout(
        endpoint,
        {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(body),
        },
        45000
      );
      if (response.ok) {
        return data;
      }

      const errMsg = parseErrorMessage(data, `Request failed with status ${response.status}`);
      lastError = new Error(errMsg);

      // Retry on strict body-validation failures with a narrower payload shape.
      const isBodyValidationFailure =
        typeof errMsg === 'string' &&
        (errMsg.toLowerCase().includes('invalid request body') ||
          errMsg.toLowerCase().includes('invalid json') ||
          errMsg.toLowerCase().includes('cannot unmarshal'));

      if (!isBodyValidationFailure) {
        throw lastError;
      }
    }

    throw lastError || new Error('Unable to load lesson content.');
  },

  async generateSlideContentForTopic(payload) {
    const { response, data } = await fetchJsonWithTimeout(
      `${API_BASE_URL}/lessons/animated-slide-content`,
      {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      },
      45000
    );
    if (!response.ok) {
      throw new Error(parseErrorMessage(data, `Request failed with status ${response.status}`));
    }

    return data;
  },

  async generateSceneSlideContentForTopic(payload) {
    const currentTopic = payload?.currentTopic
      ? compactObject({
          id: payload.currentTopic.id,
          title: payload.currentTopic.title,
          type: payload.currentTopic.type,
        })
      : undefined;
    const previousTopic = payload?.previousTopic
      ? compactObject({
          id: payload.previousTopic.id,
          title: payload.previousTopic.title,
        })
      : undefined;
    const nextTopic = payload?.nextTopic
      ? compactObject({
          id: payload.nextTopic.id,
          title: payload.nextTopic.title,
        })
      : undefined;

    const attempts = [
      compactObject(payload),
      compactObject({
        lessonId: payload?.lessonId || '',
        courseTitle: payload?.courseTitle || '',
        lessonObjective: payload?.lessonObjective || '',
        learner: payload?.learner,
        animation: payload?.animation,
        currentTopic,
        previousTopic,
        nextTopic,
        presentationMode: 'scene',
      }),
      compactObject({
        lessonId: payload?.lessonId || '',
        currentTopic,
        previousTopic,
        nextTopic,
        presentationMode: 'scene',
      }),
      compactObject({
        topicId: payload?.currentTopic?.id || payload?.topicId || '',
        topicTitle: payload?.currentTopic?.title || '',
        presentationMode: 'scene',
      }),
    ].filter(Boolean);

    let lastError = null;
    for (let i = 0; i < attempts.length; i++) {
      const body = attempts[i];
      try {
        return await postJSONWithRateLimitRetry(
          `${API_BASE_URL}/animated-video/node-content`,
          body,
          { timeoutMs: 60000 }
        );
      } catch (error) {
        const errMsg = error?.message || 'Failed to load scene node content.';
        lastError = new Error(errMsg);
        if (typeof window !== 'undefined' && window?.console?.warn) {
          window.console.warn('[AnimatedLessonService] node-content attempt failed', {
            attempt: i + 1,
            currentTopicId: payload?.currentTopic?.id || null,
            error: errMsg,
            body,
          });
        }

        const isBodyValidationFailure =
          typeof errMsg === 'string' &&
          (errMsg.toLowerCase().includes('invalid request body') ||
            errMsg.toLowerCase().includes('invalid json') ||
            errMsg.toLowerCase().includes('cannot unmarshal'));

        if (!isBodyValidationFailure) break;
      }
    }

    throw lastError || new Error('Unable to load scene node content.');
  },

  async generateSlideContentsFromGraph(context) {
    const graph = Array.isArray(context?.knowledgeGraph) ? context.knowledgeGraph : [];
    if (graph.length === 0) return [];

    const requests = graph.map((node, index) => {
      const previousTopic = index > 0 ? graph[index - 1] : null;
      const nextTopic = index < graph.length - 1 ? graph[index + 1] : null;

      return this.generateSlideContentForTopic({
        lessonId: context.lessonId || '',
        courseTitle: context.courseTitle || '',
        lessonObjective: context.lessonObjective || '',
        learner: context.learner || {
          name: '',
          educationLevel: '',
          interests: '',
          careerGoals: '',
        },
        animation: context.animation || {
          mode: 'chalkboard',
          requireLevelOrder: true,
          includePrerequisiteEdges: true,
        },
        currentTopic: node,
        previousTopic,
        nextTopic,
      });
    });

    const settled = await Promise.allSettled(requests);
    return settled.map((result) => (result.status === 'fulfilled' ? result.value : null));
  },

  async generateSceneTreeSlide(payload) {
    const attempts = [
      payload,
      {
        topic: payload?.topic || '',
        courseTitle: payload?.courseTitle || '',
        lessonObjective: payload?.lessonObjective || '',
        learner: payload?.learner || {
          name: '',
          educationLevel: '',
          interests: '',
          careerGoals: '',
        },
        animation: payload?.animation || {
          mode: 'chalkboard',
          requireLevelOrder: true,
          includePrerequisiteEdges: true,
        },
        presentationMode: 'scene',
      },
      {
        topic: payload?.topic || '',
        courseTitle: payload?.courseTitle || '',
        lessonObjective: payload?.lessonObjective || '',
        presentationMode: 'scene',
      },
      {
        topic: payload?.topic || '',
        presentationMode: 'scene',
      },
    ];

    let lastError = null;
    for (const body of attempts) {
      try {
        return await postJSONWithRateLimitRetry(`${API_BASE_URL}/animated-video/tree-slide`, body, {
          timeoutMs: 60000,
        });
      } catch (error) {
        const errMsg = error?.message || 'Failed to load scene tree.';
        lastError = new Error(errMsg);

        const isBodyValidationFailure =
          typeof errMsg === 'string' &&
          (errMsg.toLowerCase().includes('invalid request body') ||
            errMsg.toLowerCase().includes('invalid json') ||
            errMsg.toLowerCase().includes('cannot unmarshal'));

        if (!isBodyValidationFailure) break;
      }
    }

    throw lastError || new Error('Unable to load scene tree.');
  },

  flattenSceneTree(treeNodes) {
    const roots = Array.isArray(treeNodes) ? treeNodes : treeNodes ? [treeNodes] : [];
    const ordered = [];
    const visit = (node, parentId = null) => {
      if (!node || !node.id) return;
      ordered.push({
        id: String(node.id),
        title: String(node.title || node.id),
        type: String(node.type || 'concept'),
        physics: node.physics || null,
        timeline: node.timeline || null,
        children: Array.isArray(node.children) ? node.children : [],
        parentId: parentId || null,
      });
      const children = Array.isArray(node.children) ? node.children : [];
      children.forEach((child) => visit(child, String(node.id)));
    };
    roots.forEach((root) => visit(root, null));
    return ordered;
  },

  buildKnowledgeGraphFromSceneTree(treeNodes, nodeDetailsById) {
    const flat = this.flattenSceneTree(treeNodes);
    return flat.map((node) => {
      const detail = nodeDetailsById.get(node.id) || null;
      return {
        id: node.id,
        title: node.title,
        summary: detail?.explanation || '',
        keyPoints: Array.isArray(detail?.keyPoints) ? detail.keyPoints : [],
        examples: Array.isArray(detail?.examples) ? detail.examples : [],
        prerequisites: node.parentId ? [node.parentId] : [],
        detail,
      };
    });
  },

  async generateSceneSlidesFromTree(context) {
    const treeResponse = await this.generateSceneTreeSlide({
      topic: context.topic || '',
      courseTitle: context.courseTitle || '',
      lessonObjective: context.lessonObjective || '',
      learner: context.learner || {
        name: '',
        educationLevel: '',
        interests: '',
        careerGoals: '',
      },
      animation: context.animation || {
        mode: 'chalkboard',
        requireLevelOrder: true,
        includePrerequisiteEdges: true,
      },
      presentationMode: 'scene',
    });

    const treeNodes =
      treeResponse?.treeSlides || treeResponse?.tree || treeResponse?.nodes || treeResponse?.root;
    const flat = this.flattenSceneTree(treeNodes);
    if (flat.length === 0) {
      throw new Error('Scene tree is empty or invalid.');
    }

    const lessonId = treeResponse?.lessonId || context.lessonId || '';
    const lessonTitle = treeResponse?.title || context.topic || 'Scene Lesson';
    const detailsById = new Map();

    const buildPayloadForNode = (node, index) => {
      const previous = index > 0 ? flat[index - 1] : null;
      const next = index < flat.length - 1 ? flat[index + 1] : null;
      return {
        lessonId,
        courseTitle: context.courseTitle || '',
        lessonObjective: context.lessonObjective || '',
        learner: context.learner || null,
        animation: context.animation || null,
        currentTopic: { id: node.id, title: node.title, type: node.type },
        previousTopic: previous ? { id: previous.id, title: previous.title } : null,
        nextTopic: next ? { id: next.id, title: next.title } : null,
        presentationMode: 'scene',
        scenePolicy: {
          requireScene: true,
          requireTimeline: true,
          requireAnimationPlan: true,
        },
      };
    };

    const hydrateNodeDetail = async (node, index) => {
      const retryCount = Math.max(
        1,
        Math.min(4, Number.isFinite(context?.nodeRetryCount) ? Number(context.nodeRetryCount) : 2)
      );
      const retryBaseMs = Math.max(
        250,
        Number.isFinite(context?.nodeRetryBaseMs) ? Number(context.nodeRetryBaseMs) : 500
      );

      let lastError = null;
      for (let attempt = 0; attempt < retryCount; attempt += 1) {
        try {
          const detail = await this.generateSceneSlideContentForTopic(
            buildPayloadForNode(node, index)
          );
          detailsById.set(
            node.id,
            this.enrichDemoDetailForTopic(context.topic, node, detail, index)
          );
          return;
        } catch (error) {
          lastError = error;
          if (attempt >= retryCount - 1) break;
          const jitterMs = Math.floor(Math.random() * 250);
          const backoffMs = retryBaseMs * 2 ** attempt + jitterMs;
          await sleep(backoffMs);
        }
      }

      throw new Error(
        `Node "${node.title}" (${node.id}) failed after ${retryCount} retry attempts: ${
          lastError?.message || 'unknown error'
        }`
      );
    };

    const nodeConcurrency = Math.max(
      1,
      Math.min(
        4,
        Number.isFinite(context?.nodeConcurrency)
          ? Number(context.nodeConcurrency)
          : typeof window !== 'undefined' && window.innerWidth <= 768
            ? 2
            : 3
      )
    );

    await runWithConcurrency(
      flat,
      async (node, index) => {
        await hydrateNodeDetail(node, index).catch((error) => {
          throw new Error(
            `Failed to produce valid slide content for "${node.title}" (${node.id}): ${
              error?.message || 'unknown error'
            }`
          );
        });
      },
      nodeConcurrency
    );

    return {
      lessonId,
      title: lessonTitle,
      knowledgeGraph: this.buildKnowledgeGraphFromSceneTree(treeNodes, detailsById),
    };
  },
};

export default AnimatedLessonService;
